import { BackendClient, Email, ShareLinkId, trackError } from 'backend-client-web';
import { DomainError } from 'domain-errors';
import {
  jsonParseThaw,
  jsonStringifyFreeze,
  LoginVault2,
  Unprotect,
  UnprotectedValue,
} from 'persistable-types';
import {
  SymEncryptedBlob,
  SymEncryptionKey,
  decodeUTF8,
  encodeUTF8,
  loadSymEncryptedBlob,
  loadSymEncryptedSymmetricEncryptionKey,
  loadSymEncryptionKey,
  newSymmetricSecret,
  symDecrypt,
  symDecryptSymmetricEncryptionKey,
  symEncrypt,
  symEncryptSymmetricEncryptionKey,
  decodeBase64Urlsafe,
} from 'vault-crypto';

import { ClientCoreErrorCode, InvalidShareLinkError } from '../errors';
import protect from '../ops/protect';
import unprotect from '../ops/unprotect';
import { Login } from '../vaultContent/login';
import {
  recursivelyTransformProtectedValues,
  recursivelyTransformUnprotectedValues,
} from '../vaultContent/modifyProtectedValues';

/** `baseUrl` includes the path prefix of the share link page, if any. */
export function generateShareLink(
  baseUrl: string,
  shareLinkId: string,
  secretBase64: string,
): string {
  return `${baseUrl}/${shareLinkId}#${secretBase64}`;
}

function parseShareLink(shareLink: string): { shareLinkId: ShareLinkId; secret: SymEncryptionKey } {
  try {
    const url = new URL(shareLink);

    const pathParts = url.pathname.split('/');
    const shareLinkId = pathParts[pathParts.length - 1];
    const secretBase64 = url.hash.substring(1);
    if (!secretBase64) {
      throw new InvalidShareLinkError();
    }
    const secret = loadSymEncryptionKey(decodeBase64Urlsafe(secretBase64));

    return { shareLinkId, secret };
  } catch (e) {
    if (e instanceof DomainError && e.code === ClientCoreErrorCode.InvalidShareLink) {
      // Rethrow InvalidShareLink errors
      throw e;
    }
    // Handle any other error during link parsing as Invalid share link
    trackError(e);
    throw new InvalidShareLinkError();
  }
}

export async function encryptShareLinkLogin(
  login: LoginVault2.UnprotectedLogin,
  secret: SymEncryptionKey,
): Promise<{
  encryptedPayload: SymEncryptedBlob;
  encryptedProtectedSecret: SymEncryptedBlob;
}> {
  // history is intentionally excluded from shared logins. Omitting it from the type too
  // keeps the value JSON-serializable (history.revisions is otherwise unparsed `unknown`).
  const strippedLogin: Omit<LoginVault2.UnprotectedLogin, 'history'> = {
    customFields: login.customFields,
    note: login.note,
    password: login.password,
    title: login.title,
    uiType: login.uiType,
    username: login.username,
    websites: login.websites,
    androidApps: login.androidApps,
    scope: login.scope,
    creditCard: login.creditCard,
    totp: login.totp,
    websiteAttributes: login.websiteAttributes,
    wifiSsid: login.wifiSsid,
    displayHeadline: login.displayHeadline,
    totpAlgorithm: login.totpAlgorithm,
    totpDigits: login.totpDigits,
    totpPeriod: login.totpPeriod,
    elsterCertificate: login.elsterCertificate,
    // NOTE: the following fields are explicitly redacted
    creationTime: '1970-01-01T00:00:00.000Z',
    editTime: '1970-01-01T00:00:00.000Z',
    changeTime: '1970-01-01T00:00:00.000Z',
  };

  const protectedSecret = newSymmetricSecret();

  const protectedLogin: Omit<Login, 'history'> = recursivelyTransformUnprotectedValues(
    strippedLogin,
    (pv) => protect(protectedSecret, pv),
  );

  const loginJson = jsonStringifyFreeze(protectedLogin);

  const encryptedPayload = symEncrypt(secret, decodeUTF8(loginJson));
  const encryptedProtectedSecret = await symEncryptSymmetricEncryptionKey(secret, protectedSecret);

  return { encryptedPayload, encryptedProtectedSecret };
}

function decryptShareLinkLogin(
  secret: SymEncryptionKey,
  encryptedPayload: Uint8Array,
): LoginVault2.Login {
  const payload = symDecrypt(secret, loadSymEncryptedBlob(encryptedPayload));
  return LoginVault2.Login.parse(jsonParseThaw(encodeUTF8(payload)));
}

export async function previewShareLink(
  bc: BackendClient,
  shareLink: string,
): Promise<{
  login: LoginVault2.Login;
  shareLinkId: ShareLinkId;
  senderEmail: Email;
  organizationName?: string;
  expiresAt?: Date;
}> {
  const { shareLinkId, secret } = parseShareLink(shareLink);

  const { encryptedPayload, senderEmail, organizationName, expiresAt } =
    await bc.shareLinks.preview(shareLinkId);

  try {
    const login = decryptShareLinkLogin(secret, encryptedPayload);

    return { login, shareLinkId, senderEmail, organizationName, expiresAt };
  } catch (e) {
    // Handle any error during decryption as Invalid share link
    trackError(e);
    throw new InvalidShareLinkError();
  }
}

export async function openShareLink(
  bc: BackendClient,
  shareLink: string,
): Promise<{
  login: Unprotect<Login, UnprotectedValue>;
  shareLinkId: ShareLinkId;
  senderEmail: Email;
  organizationName?: string;
}> {
  const { shareLinkId, secret } = parseShareLink(shareLink);

  const { encryptedPayload, encryptedProtectedSecret, senderEmail, organizationName } =
    await bc.shareLinks.show(shareLinkId);

  try {
    const protectedLogin = decryptShareLinkLogin(secret, encryptedPayload);

    const protectedSecret = await symDecryptSymmetricEncryptionKey(
      secret,
      loadSymEncryptedSymmetricEncryptionKey(encryptedProtectedSecret),
    );

    let login = recursivelyTransformProtectedValues<UnprotectedValue, Login>(
      protectedLogin,
      (pv) => ({
        contentId: pv.contentId,
        unencrypted: unprotect(protectedSecret, pv),
      }),
    );

    login = {
      ...login,
      passwordOrigin: {
        type: 'shareLink' as const,
        passwordContentId: login.password.contentId,
        senderEmail,
      },
      creationTime: new Date().toISOString(),
    };

    return { login, shareLinkId, senderEmail, organizationName };
  } catch (e) {
    // Handle any error during decryption as Invalid share link
    trackError(e);
    throw new InvalidShareLinkError();
  }
}
